import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { Box, Button, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import type { EnrollmentShape, OpenEnrollmentPhase } from '../types';

type HeroKeys = {
  eyebrow: string;
  title: string;
  body: string;
  cta: string;
  /** CTA label when the target task is already started (resume wording). */
  ctaResume: string;
};

const HERO_KEYS: Record<OpenEnrollmentPhase, HeroKeys> = {
  prescreen: {
    eyebrow: 'EnrollmentEyebrowPrescreen',
    title: 'EnrollmentHeroTitlePrescreen',
    body: 'EnrollmentHeroBodyPrescreen',
    cta: 'EnrollmentHeroCtaPrescreen',
    ctaResume: 'EnrollmentHeroCtaPrescreenResume',
  },
  consent: {
    eyebrow: 'EnrollmentEyebrowConsent',
    title: 'EnrollmentHeroTitleConsent',
    body: 'EnrollmentHeroBodyConsent',
    cta: 'EnrollmentHeroCtaConsent',
    ctaResume: 'EnrollmentHeroCtaConsentResume',
  },
};

/** HERO_KEYS carries the two-step copy; single-step shapes overlay prefix-free
 *  variants (no "Step N of 2", no eligibility framing). */
const HERO_KEY_OVERRIDES: Partial<
  Record<
    EnrollmentShape,
    Partial<Record<OpenEnrollmentPhase, Partial<HeroKeys>>>
  >
> = {
  'prescreener-only': {
    prescreen: { eyebrow: 'EnrollmentEyebrowPrescreenOnly' },
  },
  'icf-only': {
    consent: {
      eyebrow: 'EnrollmentEyebrowConsentOnly',
      title: 'EnrollmentHeroTitleConsentOnly',
    },
  },
};

type EnrollmentHeroProps = {
  phase: OpenEnrollmentPhase;
  shape: EnrollmentShape;
  onCta?: (() => void) | undefined;
  /** Renders the CTA disabled while the schedule resolves its target. */
  isCtaResolving?: boolean | undefined;
  /** Switches the CTA to resume wording for an already-started task. */
  isCtaResuming?: boolean | undefined;
};

/** Opaque primary-tinted hero panel with a thin primary border — one next step per phase. */
export const EnrollmentHero = ({
  phase,
  shape,
  onCta,
  isCtaResolving = false,
  isCtaResuming = false,
}: EnrollmentHeroProps) => {
  const { t } = useTranslation();
  const keys = { ...HERO_KEYS[phase], ...HERO_KEY_OVERRIDES[shape]?.[phase] };

  return (
    <Box
      data-testid="enrollment-hero"
      sx={theme => ({
        backgroundColor: theme.ui.primarySurface,
        border: `1px solid ${alpha(theme.palette.primary.main, 0.22)}`,
        borderRadius: `${String(theme.ui.cardRadius)}px`,
        p: { xs: 2.25, md: 2.75 },
      })}
    >
      <Typography
        sx={{
          fontSize: 12.5,
          fontWeight: 700,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: 'primary.main',
        }}
      >
        {t(keys.eyebrow)}
      </Typography>
      <Typography
        component="h1"
        sx={{
          fontSize: 26,
          fontWeight: 800,
          letterSpacing: '-0.02em',
          color: 'text.primary',
          mt: 1.25,
          textWrap: 'pretty',
        }}
      >
        {t(keys.title)}
      </Typography>
      <Typography
        sx={{
          fontSize: 15,
          color: 'text.secondary',
          lineHeight: 1.55,
          mt: 1.5,
          maxWidth: 520,
          textWrap: 'pretty',
        }}
      >
        {t(keys.body)}
      </Typography>
      {(onCta != null || isCtaResolving) && (
        <Button
          variant="contained"
          onClick={onCta}
          disabled={onCta == null}
          aria-busy={isCtaResolving || undefined}
          endIcon={<ArrowForwardIosIcon sx={{ fontSize: 15 }} />}
          sx={{
            mt: 2.5,
            minHeight: 48,
            px: 2.75,
            borderRadius: 999,
            textTransform: 'none',
            fontSize: 15,
            fontWeight: 700,
          }}
        >
          {t(isCtaResuming ? keys.ctaResume : keys.cta)}
        </Button>
      )}
    </Box>
  );
};
